import { ReactNode, useEffect } from "react";
import { ItemWithMetaData } from "../api/Items";
import type { BlueprintInfo } from "../api/Blueprints";

import LeftBorderedContainer from "../shared-components/LeftBorderedContainer";
import {
  useDeleteProfilePosts,
  useUpdateItemManualScrape,
} from "../api/ProfilesApi";
import { SCRAPE_STATUS } from "../api/Profiles";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import {
  ManualProfileRowData,
  ManualProfileRowType,
  MANUAL_PROFILE_ACTION,
} from "./ManualProfileRowData";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useSalvageYardScrape } from "../api/ItemsApi";
import { SocialIcon } from "../utils/social-icon";

type ProfileRowProps = {
  reportUUID: string;
  item: ItemWithMetaData;
  rowType: ManualProfileRowType;
  onAction?: (action: MANUAL_PROFILE_ACTION, itemUUID: string) => void;
  className?: string;
  children?: ReactNode;
  isReadOnly?: boolean;
  blueprint?: BlueprintInfo | any;
  activeScrapeProfileId?: string | null;
};

function getRowColorByType(rowType: ManualProfileRowType) {
  switch (rowType) {
    case ManualProfileRowType.MANUAL:
      return "green";
    case ManualProfileRowType.SYSTEM:
      return "blue";
    case ManualProfileRowType.SKIPPED:
      return "orange";
    default:
      return "red";
  }
}

export function ManualProfileRow({
  reportUUID,
  item,
  rowType,
  className,
  children,
  isReadOnly,
  onAction,
  blueprint,
  activeScrapeProfileId,
}: ProfileRowProps) {
  const profileUrl = item.item.content.url || "";
  const {
    isLoading: isUpdatingProfile,
    mutate: updateProfile,
    error: errorUpdatingProfile,
  } = useUpdateItemManualScrape();

  const { mutate: deleteProfilePosts } = useDeleteProfilePosts();

  const {
    salvageYardScrape,
    isSalvageYardConnected,
    storingPosts,
    success,
    error,
    storedPosts,
    skippedPosts,
  } = useSalvageYardScrape();

  useEffect(() => {
    if (success) handleAction(MANUAL_PROFILE_ACTION.COMPLETE);
  }, [success]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) handleAction(MANUAL_PROFILE_ACTION.RESET);
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleAction(action: MANUAL_PROFILE_ACTION) {
    switch (action) {
      case MANUAL_PROFILE_ACTION.SKIP:
        {
          const isActive = activeScrapeProfileId === item.item.external_id;
          const newStatus = isActive
            ? SCRAPE_STATUS.DONE
            : SCRAPE_STATUS.SKIPPED;
          updateProfile({
            profile: item.item,
            status: newStatus,
          });
          if (onAction) {
            onAction(
              isActive
                ? MANUAL_PROFILE_ACTION.COMPLETE
                : MANUAL_PROFILE_ACTION.SKIP,
              item.item.external_id
            );
          }
        }
        break;

      case MANUAL_PROFILE_ACTION.START:
      case MANUAL_PROFILE_ACTION.RESTART:
        if (
          activeScrapeProfileId &&
          activeScrapeProfileId !== item.item.external_id
        )
          return;
        if (profileUrl) window.open(profileUrl);
        item.item.content.scrape_status = SCRAPE_STATUS.IN_PROGRESS;
        updateProfile({
          profile: item.item,
          status: SCRAPE_STATUS.IN_PROGRESS,
        });
        if (onAction) {
          onAction(action, item.item.external_id);
        }
        if (action === MANUAL_PROFILE_ACTION.RESTART) {
          deleteProfilePosts(item.item.external_id);
        }
        salvageYardScrape(
          reportUUID,
          item.item.external_id,
          item.item.content,
          blueprint
        );
        break;

      case MANUAL_PROFILE_ACTION.COMPLETE:
        item.item.content.scrape_status = SCRAPE_STATUS.DONE;
        updateProfile({
          profile: item.item,
          status: SCRAPE_STATUS.DONE,
        });
        if (onAction) {
          onAction(MANUAL_PROFILE_ACTION.COMPLETE, item.item.external_id);
        }
        break;

      case MANUAL_PROFILE_ACTION.RESET:
        item.item.content.scrape_status = SCRAPE_STATUS.TO_DO;
        updateProfile({
          profile: item.item,
          status: SCRAPE_STATUS.TO_DO,
        });
        if (onAction) {
          onAction(MANUAL_PROFILE_ACTION.RESET, item.item.external_id);
        }
        break;
    }
  }

  const tabColor = getRowColorByType(rowType);
  const disableStart =
    !!isReadOnly ||
    (!!activeScrapeProfileId &&
      activeScrapeProfileId !== item.item.external_id);

  return (
    <>
      <div className="bg-white my-3">
        <LeftBorderedContainer
          color={tabColor}
          className={`${className} border-y-gray-300 border-r-gray-300 pr-4`}
          withOverflow={false}
        >
          <div className="flex w-full items-center">
            <div>
              <SocialIcon url={profileUrl} />
            </div>
            <div className="flex-1 truncate pl-2">
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-normal leading-6 truncate min-w-0"
                title="Open profile"
              >
                {profileUrl}
              </a>
            </div>
            <div>
              {errorUpdatingProfile && (
                <ExclamationCircleIcon className="w-4 h-4 text-red-400" />
              )}
              {isUpdatingProfile ? (
                <LoadingSpinner></LoadingSpinner>
              ) : (
                <ManualProfileRowData
                  profile={item}
                  rowType={rowType}
                  isSalvageYardConnected={isSalvageYardConnected}
                  storingPosts={storingPosts}
                  scrapeError={error || ""}
                  isReadOnly={!!isReadOnly}
                  storedPosts={storedPosts}
                  skippedPosts={skippedPosts}
                  onAction={handleAction}
                  disableStart={disableStart}
                />
              )}
            </div>
          </div>
        </LeftBorderedContainer>
      </div>
    </>
  );
}
